import { useEffect, useState } from 'preact/hooks';
import { CaptureGlyph as Knot } from '../components/CaptureGlyph';
import { Onboarding } from './Onboarding';
import { setStoredTenantId, setStoredTenantVertical } from '../lib/tenant';
import { listTenants, ApiError, type ApiUser, type TenantMembership } from '../lib/api';
import './TenantResolver.css';

export interface TenantResolverProps {
  /** The signed-in user; forwarded to Onboarding when there's no workspace. */
  user: ApiUser;
  /** Carried through to Onboarding (the fresh-registration email-confirm note). */
  notice?: string;
  /** Called with the chosen/created tenantId once resolved; App stores it as
   *  active and renders the shell. */
  onResolved: (tenantId: string) => void;
  /** Forwarded to Onboarding: the signed-in user, after onboarding saved the
   *  name they typed on its first screen (2026-09-15). App holds that user in
   *  state, so without this the greeting stays on the old value. */
  onUserUpdated?: (user: ApiUser) => void;
}

type State =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'onboarding' }
  /** ⚑ ONE HOUSEHOLD, AND IT NEVER FINISHED BEING SET UP (2026-09-16). The
   *  auto-select below is the right answer for a household that is DONE — and
   *  it was being given to one that was interrupted, which is how a reload
   *  part-way through onboarding landed on Today with the derived address
   *  taken and the passkey offer never made. App's own gate catches the
   *  ordinary reload (the stored id never reaches this screen); this catches
   *  the same person on a browser that has no stored id — a second device, a
   *  cleared site, a private window. */
  | { phase: 'resume'; tenantId: string }
  | { phase: 'picker'; tenants: TenantMembership[] };

// Auth-2: tenant resolution after auth, replacing the paste-an-id
// ConnectWorkspace stopgap. GET /auth/tenants -> exactly one auto-selects
// straight to Today (no id to paste, ever); none drops into first-run
// onboarding; several show a calm workspace picker. See lib/tenant.ts for how
// the chosen id then travels as X-Tenant-Id on every /api call.
export function TenantResolver({ user, notice, onResolved, onUserUpdated }: TenantResolverProps) {
  const [state, setState] = useState<State>({ phase: 'loading' });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function resolve() {
      try {
        const tenants = await listTenants();
        if (cancelled) return;
        if (tenants.length === 1) {
          const only = tenants[0]!;
          if (only.setupInProgress === true && only.role === 'owner') {
            // Not stored yet, and deliberately: the id is adopted when setting
            // up actually finishes, by the same call that says so.
            setStoredTenantVertical(only.vertical);
            setState({ phase: 'resume', tenantId: only.tenantId });
            return;
          }
          setStoredTenantId(only.tenantId);
          // C1: GET /auth/tenants has always carried each membership's
          // vertical; this screen used to read it and drop it on the floor.
          // The operator shell's pack skin is chosen from it (lib/skin.ts),
          // so it now travels alongside the id.
          setStoredTenantVertical(only.vertical);
          onResolved(only.tenantId);
          return;
        }
        if (tenants.length === 0) {
          setState({ phase: 'onboarding' });
          return;
        }
        setState({ phase: 'picker', tenants });
      } catch (err) {
        if (cancelled) return;
        setState({
          phase: 'error',
          message: err instanceof ApiError ? err.message : 'Could not reach the server.',
        });
      }
    }

    void resolve();
    return () => {
      cancelled = true;
    };
    // Resolve once per mount — App unmounts this the moment a tenant is set.
    // The nonce re-runs it on "Read it again": the failure is a failed READ
    // (network, a server that couldn't answer), so the same read is the fix.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce]);

  if (state.phase === 'loading') {
    return (
      <div class="tenant">
        <div class="tenant__center">
          <Knot size={46} />
          <div class="tenant__say tenant__say--tight">One moment…</div>
        </div>
      </div>
    );
  }

  if (state.phase === 'error') {
    return (
      <div class="tenant">
        <div class="tenant__center">
          <Knot size={46} />
          <div class="tenant__say tenant__say--tight">
            Couldn’t load your workspaces.
            <br />
            <span class="tenant__say-soft">{state.message}</span>
          </div>
          <button
            type="button"
            class="tenant__item"
            style="justify-content:center;margin-top:16px"
            onClick={() => {
              setState({ phase: 'loading' });
              setNonce((n) => n + 1);
            }}
          >
            <span class="tenant__item-name">Read it again</span>
          </button>
        </div>
      </div>
    );
  }

  if (state.phase === 'onboarding') {
    return (
      <Onboarding
        user={user}
        notice={notice}
        onResolved={onResolved}
        {...(onUserUpdated ? { onUserUpdated } : {})}
      />
    );
  }

  if (state.phase === 'resume') {
    return (
      <Onboarding
        user={user}
        resumeTenantId={state.tenantId}
        onResolved={onResolved}
        {...(onUserUpdated ? { onUserUpdated } : {})}
      />
    );
  }

  // Multiple workspaces — a calm picker.
  function pick(tenant: TenantMembership) {
    setStoredTenantId(tenant.tenantId);
    setStoredTenantVertical(tenant.vertical);
    onResolved(tenant.tenantId);
  }

  return (
    <div class="tenant">
      <div class="tenant__eyebrow">Your workspaces</div>
      <div class="tenant__say">
        Where to?
        <br />
        <span class="tenant__say-soft">Choose a workspace to open.</span>
      </div>
      <div class="tenant__list">
        {state.tenants.map((t) => (
          <button type="button" key={t.tenantId} class="tenant__item" onClick={() => pick(t)}>
            <span class="tenant__item-name">{t.name}</span>
            <span class="tenant__item-role">{t.role}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
